import { BEEDIRECT_HOST } from './../webpackConstants'
import queryString from 'query-string'
import { FormConfig } from '../types/form'
import { getTheme, applyTheme } from './theme'
import { AuthenticationError } from '../graphql/beehive-sdk'
import { createField } from '../lib/form-builder'
import { calculateLease } from '../lib/calculate-lease'
import { getCategories } from '../lib/categories'
import { createInvalidTokenErrorMessage } from '../lib/create-error-message'
import { formatCurrency } from '../lib/format'

import {
    DEFAULT_CATEGORY,
    DOWNPAYMENT_NEW,
    DOWNPAYMENT_USED,
} from '../lib/constants'
import { sendHostMessage } from '../lib/beedirect-modal'
import { CalculationQuery } from '../../__generated__/beehive'

type Boundaries = Pick<
    NonNullable<
        NonNullable<
            NonNullable<CalculationQuery['calculateLease']>['boundaries']
        >
    >,
    'downPayment' | 'tenor'
>
type CalculationError = NonNullable<
    NonNullable<NonNullable<CalculationQuery['calculateLease']>['errors']>
>[0]
;(function () {
    function getCategoryOptions(input: HTMLInputElement) {
        getCategories().then((data) => {
            if (!data.categoryGroups) {
                return
            }

            data.categoryGroups.map((category) => {
                const option = document.createElement('option')
                option.value = category.id
                option.innerText = category.name
                input.appendChild(option)
            })

            return
        })
    }

    function getSubCategoryOptions(input: HTMLInputElement) {
        if (!urlParams['category-id']) return

        getCategories().then((data) => {
            data?.categoryGroups
                ?.find(
                    (categoryGroup) =>
                        categoryGroup.id === urlParams['category-id']
                )
                ?.categories?.map((category) => {
                    const option = document.createElement('option')
                    option.value = category.id
                    option.innerText = category.name
                    input.appendChild(option)
                })

            return
        })
    }

    const errorMapping = {
        'over max tenor': 'Looptijd moet maximaal {0} zijn',
        'under min_tenor': 'Looptijd moet minimaal {0} zijn',
        'below min_down_payment': 'Aanbetaling moet minimaal {0} zijn',
        'over max_down_payment': 'Aanbetaling moet maximaal {0} zijn',
    }

    function getSpinner() {
        return '<svg class="loading__spinner version="1.1" id="loader-1" x="0px" y="0px" width="24px" height="24px" viewBox="0 0 50 50"><path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" transform="rotate(69.789 25 25)"><animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.6s" repeatCount="indefinite" /></path></svg>'
    }

    function createResult() {
        const result = document.createElement('div')
        result.id = 'result'
        result.className = 'result'

        return result
    }

    function validateFields(fields: string[], form: HTMLFormElement) {
        const errors = fields
            .filter(function (f) {
                return !form[f].value
            })
            .map(function (f) {
                return { attribute: f, message: 'Moet ingevuld zijn' }
            })

        if (form['purchasePrice'].value === '15000') {
            errors.push({
                attribute: 'purchasePrice',
                message: 'Aanschafwaarde moet minstens € 15.000 zijn',
            })
        }

        return errors
    }

    function renderLoading() {
        const element = document.getElementById('result')
        if (!element) {
            return
        }

        element.innerHTML = `<div class="loading">${getSpinner()} <p>Leaseprijs berekenen ...</p></div>`
    }

    function clearResult() {
        const element = document.getElementById('result')
        if (!element) {
            return
        }

        element.innerHTML = ''
    }

    function clearErrors() {
        const errors = document.getElementsByClassName('error')
        while (errors.length > 0 && errors[0].parentNode) {
            errors[0].parentNode.removeChild(errors[0])
        }
    }

    function getErrorMessage(error: CalculationError, boundaries?: Boundaries) {
        if (!boundaries) {
            return error.message
        }

        let boundary = 0
        if (error.message.indexOf('max') !== -1) {
            boundary = boundaries[error.attribute as keyof Boundaries].max
        } else if (error.message.indexOf('min') !== -1) {
            boundary = boundaries[error.attribute as keyof Boundaries].min
        }

        if (
            boundary > 0 &&
            errorMapping[error.message as keyof typeof errorMapping]
        ) {
            return errorMapping[
                error.message as keyof typeof errorMapping
            ].replace('{0}', boundary.toString())
        }

        return error.message
    }

    function renderErrors(
        errors: CalculationError[],
        form: HTMLFormElement,
        boundaries?: Boundaries
    ) {
        for (let i = 0; i < errors.length; i++) {
            const error = errors[i]

            if (form[error.attribute]) {
                form[error.attribute].insertAdjacentHTML(
                    'afterend',
                    `<p class="error">${getErrorMessage(error, boundaries)}</p>`
                )
            }
        }
    }

    function openBeedirect(e: Event) {
        const form = (e.target as HTMLButtonElement)?.form
        if (!form) {
            return false
        }

        const localErrors = validateFields(
            ['purchasePrice', 'tenor', 'downPayment'],
            form
        )

        if (localErrors.length > 0) {
            renderErrors(localErrors, form)
        }

        const shownErrors = document.getElementsByClassName('error')
        if (shownErrors.length > 0) {
            return false
        }

        const queryString = new URLSearchParams()
        for (let i = 0; i < form.elements.length - 1; i++) {
            const element = form[i] as HTMLInputElement | HTMLSelectElement
            // We don't need the button value in the query string
            if (element.nodeName === 'BUTTON') continue

            queryString.append(element.name, element.value)
        }

        sendHostMessage({
            event: 'lease:open',
            value: { queryString: queryString.toString() },
        })

        return false
    }

    function renderResult(data: CalculationQuery, form: HTMLFormElement) {
        clearResult()

        if (!data?.calculateLease?.boundaries) {
            return
        }

        if (data?.calculateLease?.errors) {
            renderErrors(
                data.calculateLease.errors,
                form,
                data.calculateLease.boundaries
            )
        }

        if (!data?.calculateLease?.calculation?.monthlyPayment) {
            return
        }

        const element = document.getElementById('result')
        if (!element) {
            return
        }

        element.innerHTML =
            '<span class="result__title">Je leaset al vanaf</span>'
        element.innerHTML += `<span class="result__amount">${formatCurrency(
            data.calculateLease.calculation.monthlyPayment
        )}</span>`
        element.innerHTML += '<span class="result__period">/m</span>'

        const offerButton = document.getElementsByClassName(
            'button--secondary'
        )[0] as HTMLButtonElement
        offerButton.style.display = 'block'
    }

    function submitCalculation(e: Event) {
        clearErrors()

        const form = e.target as HTMLFormElement
        const localErrors = validateFields(
            ['purchasePrice', 'tenor', 'downPayment'],
            form
        )

        if (localErrors.length > 0) {
            renderErrors(localErrors, form)
        }

        const shownErrors = document.getElementsByClassName('error')
        if (shownErrors.length > 0) {
            return false
        }

        clearResult()

        const purchasePrice = parseInt(form['purchasePrice'].value)
        const tenor = parseInt(form['tenor'].value, 10)
        const downPayment = parseInt(form['downPayment'].value)
        const objectUsed = form['objectUsed'].value === 'true'
        const balloonPayment = parseInt(form['balloonPayment'].value)
        const year = parseInt(form['objectYear'].value, 10)
        const categoryId = form['categoryGroupId'].value
        const intermediaryToken = form['intermediaryToken'].value

        form.action = `${BEEDIRECT_HOST}/assetklasse/?categoryGroupId=${categoryId}`

        renderLoading()

        calculateLease({
            purchasePrice,
            tenor,
            downPayment,
            balloonPayment,
            objectYear: year,
            categoryId,
            objectUsed,
            intermediaryToken,
        }).then((result) => {
            if (result) return renderResult(result, form)
        })

        return false
    }

    function createButtons() {
        const container = document.createElement('div')
        container.className = 'form-buttons'

        const submitButton = document.createElement('button')
        submitButton.type = 'submit'
        submitButton.innerText = 'Prijs berekenen'
        submitButton.className = 'button  button--primary'
        submitButton.setAttribute(
            'data-hook',
            'calculator-widget-calculate-price'
        )
        container.appendChild(submitButton)

        const offerButton = document.createElement('button')
        offerButton.type = 'button'
        offerButton.innerText = 'Offerte opvragen'
        offerButton.className = 'button  button--secondary'
        submitButton.setAttribute('data-hook', 'calculator-widget-beedirect')
        offerButton.onclick = openBeedirect
        container.appendChild(offerButton)

        return container
    }

    function createHeader(logoType: LogoTypes, showLogo: boolean) {
        const header = document.createElement('header')
        header.className = 'header'

        const title = document.createElement('h3')
        title.innerHTML = 'Lease deze machine'
        header.appendChild(title)

        if (showLogo) {
            const logo = document.createElement('img')
            logo.src = `/logo--${logoType}.svg`
            logo.setAttribute('width', '75')
            logo.setAttribute('height', '22')
            header.appendChild(logo)
        }

        const intro = document.createElement('p')
        intro.innerHTML =
            'Voordelige financiering regelen? Bereken hieronder gemakkelijk je maandlasten en regel direct je persoonlijk financieringsplan.'
        header.appendChild(intro)

        return header
    }

    function formatTenorLabel(boundaries: Boundaries) {
        return `Looptijd <span class="label__help">(${
            boundaries.tenor.min !== boundaries.tenor.max
                ? `tussen ${boundaries.tenor.min} en ${boundaries.tenor.max} maanden`
                : `${boundaries.tenor.min} maanden`
        })</span>`
    }

    function formatDownpaymentLabel(boundaries: Boundaries) {
        return `Aanbetaling <span class="label__help">(tussen ${formatCurrency(
            boundaries.downPayment.min
        )} en ${formatCurrency(boundaries.downPayment.max)})</span>`
    }

    async function createWidget(urlParams: CalculatorUrlParams) {
        const theme = getTheme(urlParams)
        if (!theme) {
            throw new Error(
                'Beequip widget not rendered: invalid theme configuration'
            )
        }

        const purchasePrice = parseInt(urlParams['purchase-price'], 10)
        if (purchasePrice < 15000) {
            // eslint-disable-next-line no-console
            console.warn(
                'Beequip widget not rendered: purchase price cannot be below € 15.000'
            )
            return
        }

        const formConfig: FormConfig = {
            token: {
                attribute: 'token',
                name: 'intermediaryToken',
                type: 'hidden',
            },
            brand: {
                attribute: 'brand',
                name: 'objectBrand',
                type: 'hidden',
            },
            model: {
                attribute: 'model',
                name: 'objectType',
                type: 'hidden',
            },
            year: {
                attribute: 'year',
                name: 'objectYear',
                type: 'hidden',
            },
            category: {
                attribute: 'category-id',
                name: 'categoryGroupId',
                label: 'Categorie',
                fillMethod: getCategoryOptions,
                hideWhenValuePresent: true,
            },
            subcategory: {
                attribute: 'subcategory-id',
                name: 'categoryId',
                label: 'Subcategorie',
                fillMethod: getSubCategoryOptions,
                type: !urlParams['category-id'] ? 'hidden' : undefined,
                hideWhenValuePresent: true,
            },
            purchasePrice: {
                attribute: 'purchase-price',
                name: 'purchasePrice',
                label: 'Aanschafwaarde',
                placeholder: 'In euro’s',
                type: 'number',
                prefix: '€',
            },
            tenor: {
                attribute: 'tenor',
                name: 'tenor',
                label: formatTenorLabel,
                placeholder: 'In maanden',
                type: 'number',
            },
            downPayment: {
                attribute: 'down-payment',
                name: 'downPayment',
                label: formatDownpaymentLabel,
                placeholder: 'In euro’s',
                type: 'number',
                prefix: '€',
            },
            used: {
                attribute: 'object-used',
                name: 'objectUsed',
                type: 'hidden',
                defaultValue: 'true',
            },
            balloonPayment: {
                name: 'balloonPayment',
                type: 'hidden',
                defaultValue: '1',
            },
            referrer: {
                attribute: 'referrer',
                name: 'referrer',
                type: 'hidden',
            },
        }

        let data

        try {
            data = await calculateLease({
                purchasePrice,
                tenor: 24,
                balloonPayment: formConfig.balloonPayment.defaultValue
                    ? parseInt(formConfig.balloonPayment.defaultValue, 10)
                    : 1,
                objectYear: parseInt(urlParams.year, 10),
                downPayment:
                    purchasePrice *
                    (urlParams['object-used'] === 'true'
                        ? DOWNPAYMENT_USED
                        : DOWNPAYMENT_NEW),
                categoryId: urlParams['category-id'] ?? DEFAULT_CATEGORY,
                objectUsed:
                    urlParams['object-used'] !== undefined
                        ? urlParams['object-used'] === 'true'
                        : true,
                intermediaryToken: urlParams['token'],
            })
        } catch (error) {
            if (error instanceof AuthenticationError) {
                // eslint-disable-next-line no-console
                console.error(error)

                return createInvalidTokenErrorMessage(
                    'Ongeldig token geconfigureerd. Gebruik het token dat je van Beequip hebt ontvangen.'
                )
            }
        }

        if (
            data?.calculateLease?.boundaries?.acceptable &&
            data?.calculateLease?.errors?.length === 0
        ) {
            // eslint-disable-next-line no-console
            console.warn(
                'Beequip widget not rendered: calculation for this machine could not be made'
            )
            return
        }

        if (data?.calculateLease?.errors?.length) {
            // eslint-disable-next-line no-console
            console.warn(
                'Beequip widget not rendered: this machine cannot be leased. Reasons:',
                data?.calculateLease?.errors
            )
            return
        }

        const container = document.createElement('div')
        container.appendChild(
            createHeader(
                theme.logo,
                urlParams['show-logo'] === 'false' ? false : true
            )
        )

        const form = document.createElement('form')
        form.target = '_blank'
        form.method = 'get'
        form.onsubmit = submitCalculation
        for (const entry in formConfig) {
            const fieldConfig = formConfig[entry as keyof typeof formConfig]
            const dataValue =
                urlParams[fieldConfig.attribute as keyof typeof urlParams] ?? ''

            form.appendChild(
                createField(
                    fieldConfig,
                    dataValue,
                    data?.calculateLease?.boundaries
                )
            )
        }
        form.appendChild(createResult())
        form.appendChild(createButtons())
        container.appendChild(form)

        applyTheme(container, theme)

        return container
    }

    function validateRequiredParams(urlParams: CalculatorUrlParams) {
        return ['token', 'brand', 'model', 'year', 'purchase-price'].filter(
            (key) => !urlParams[key as keyof typeof urlParams]
        )
    }

    function createFallback() {
        const button = document.createElement('a')
        button.className = 'button  button--primary'
        button.innerText = 'Bereken je leaseprijs'
        button.href = BEEDIRECT_HOST
        button.target = '_blank'

        return button
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isIE11 = !!window.MSInputMethodContext && !!document.documentMode
    if (isIE11) {
        document.getElementsByTagName('body')[0].appendChild(createFallback())
        return
    }

    const urlParams = queryString.parse(location.search) as CalculatorUrlParams
    const invalidParamKeys = validateRequiredParams(urlParams)
    if (invalidParamKeys.length !== 0) {
        throw new Error(
            `Beequip widget not rendered due to missing required parameters: '${invalidParamKeys.join(
                ', '
            )}'`
        )
    }

    createWidget(urlParams).then((widget) => {
        if (widget) {
            document.getElementsByTagName('body')[0].appendChild(widget)

            window.dataLayer?.push({
                widgetHost: window.location.href,
                widgetType: 'calculator',
            })
        }
    })
})()
